import { Badge, Button, EmptyState, FormField, Spinner } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import { PackageCheck, QrCode } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { pickingKeys, receptionApi } from '../api';
import { ShipmentStatusBadge } from '../components/StatusBadge';

/**
 * `/portal/receive` — the restaurant's reception inbox (spec §5.18).
 *
 * The primary path is the QR on the packing slip, which deep-links straight to
 * `/portal/receive/<token>`. This list is the fallback for a torn label and the
 * place a restaurant sees what is on its way. Phone-first: 390px wide is the
 * design target, so everything is a stacked card, not a table.
 */
export function ReceiveListPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const navigate = useNavigate();
  const [token, setToken] = useState('');

  const inbox = useQuery({ queryKey: pickingKeys.reception, queryFn: receptionApi.inbox });
  useLiveQuery(['shipments', 'receptions'], pickingKeys.reception);

  return (
    <>
      <PageHeader title={t('reception.title')} subtitle={t('reception.subtitle')} />

      <form
        className="mb-6 flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (token.trim()) void navigate(`/portal/receive/${token.trim()}`);
        }}
      >
        <FormField
          label={t('reception.scanTitle')}
          className="min-w-56 flex-1"
          value={token}
          hint={<QrCode className="size-4" aria-hidden />}
          onChange={(event) => {
            setToken(event.target.value);
          }}
        />
        <Button type="submit" disabled={token.trim().length === 0}>
          {t('reception.shipment')}
        </Button>
      </form>
      <p className="mb-6 text-sm text-ink-muted">{t('reception.scanHint')}</p>

      {inbox.isLoading ? <Spinner /> : null}

      {!inbox.isLoading && (inbox.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<PackageCheck />}
          title={t('reception.empty')}
          description={t('reception.emptyDescription')}
        />
      ) : null}

      <ul className="flex flex-col gap-3" data-testid="reception-inbox">
        {inbox.data?.map((delivery) => (
          <li key={delivery.shipmentId}>
            <button
              type="button"
              className="flex w-full flex-col gap-2 rounded-xl border border-surface-border bg-surface p-4 text-left transition-colors hover:border-brand-400"
              onClick={() => {
                void navigate(`/portal/receive/${delivery.qrToken}`);
              }}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold">{delivery.shipmentNo}</span>
                <ShipmentStatusBadge status={delivery.status} />
                {delivery.isConfirmed ? (
                  <Badge tone="success" size="sm">
                    {t('reception.eventKind.confirmed')}
                  </Badge>
                ) : null}
              </div>
              <div className="text-sm text-ink-muted">
                <span className="font-mono">{delivery.orderNo}</span> ·{' '}
                {t('reception.expected')} {formatDate(delivery.deliveryDate)}
                {delivery.shippedAt ? ` · ${formatDateTime(delivery.shippedAt)}` : ''}
              </div>
              <div className="text-xs text-ink-muted">
                {t('reception.pallets')}: {delivery.pallets.length} · {t('reception.lines')}:{' '}
                {delivery.lines.length}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
